import { faThumbsUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import SellerProPlusBadge from "@/mk2/apps/bazaar/components/SellerProPlusBadge";
import { SellerEntity } from "@/mk2/apps/bazaar/schemas";
import { bazaarMyBazaarUrl, bazaarSellerUrl } from "@/mk2/apps/bazaar/urls";
import { blogsProfileUrl } from "@/mk2/apps/blogs/urls";
import { AvatarImg } from "@/mk2/components/AvatarImg";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Dot } from "@/mk2/components/Dot";
import { Link } from "@/mk2/components/Link";
import { Panel } from "@/mk2/components/Panel";
import { TrimContent } from "@/mk2/components/TrimContent";
import Like, { LikeType } from "@/mk2/containers/Like/Like";
import { url } from "@/mk2/helpers/urls";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import {
    BZ2_HIDE_TERMS,
    BZ2_SHOW_COMPLETE_TERMS,
    BZ2_USER_DETAILS_SHOW_PROFILE,
    USER_LIKE,
    USER_UNLIKE,
} from "@/mk/autogenerated/translation";
import { linebreaksbr } from "@/mk/bazaar/common/filters";
import { adminclub_bazaar_url } from "@/mk/urls/functions";

import styles from "./SellerDetails.mscss";

interface OwnProps {
    className?: string;
    seller: SellerEntity;
    isSeller?: boolean;
    isAdmin?: boolean;
    location: HistoryLocationWithState;
}

type Props = OwnProps;

const SellerDetails: React.FunctionComponent<Props> = ({
    className,
    seller,
    isSeller = false,
    isAdmin = false,
    location,
}) => {
    const user = seller.user;

    const likeButton = !isSeller ? (
        <Like
            entity="users.profile"
            id={seller.user.id}
            type={LikeType.IconLabel}
            location={location}
        >
            {({ isLiked, onLikeToggle }) => (
                <Btn
                    type={isLiked ? BtnType.BlueOutline : BtnType.Blue}
                    label={isLiked ? USER_UNLIKE : USER_LIKE}
                    icon={!isLiked ? faThumbsUp : undefined}
                    onClick={onLikeToggle}
                />
            )}
        </Like>
    ) : null;

    const details = (
        <>
            {seller.location}
            <Dot />
            <span className={styles.SellerDetails__header__info__details__like}>
                {seller.user.likesCount} <FontAwesomeIcon icon={faThumbsUp} />
            </span>
            <Dot />
            <Link to={url(blogsProfileUrl, { username: user.username })}>
                {BZ2_USER_DETAILS_SHOW_PROFILE}
            </Link>
            {isAdmin && (
                <>
                    <Dot />
                    <Link
                        to={adminclub_bazaar_url({ username: user.username })}
                        className={styles.SellerDetails__header__info__details__adminclub}
                    >
                        Seller details
                    </Link>
                </>
            )}
        </>
    );

    const bazaarUrl = isSeller
        ? url(bazaarMyBazaarUrl, { username: user.username })
        : url(bazaarSellerUrl, { username: user.username });

    // Run function directly - we need to know if not empty
    const badge = SellerProPlusBadge({ isSeller, user });

    return (
        <Panel className={cx(styles.SellerDetails, className)}>
            <div className={styles.SellerDetails__header}>
                <AvatarImg
                    size={50}
                    user={user}
                    href={bazaarUrl}
                    className={styles.SellerDetails__header__avatar}
                />
                <div className={styles.SellerDetails__header__info}>
                    <div className={styles.SellerDetails__header__info__username}>
                        <Link
                            to={bazaarUrl}
                            className={styles.SellerDetails__header__info__username__link}
                        >
                            {user.username}
                        </Link>
                    </div>
                    <div className={styles.SellerDetails__header__info__details}>
                        {badge}
                        <span
                            className={cx(
                                styles.SellerDetails__header__more,
                                styles["SellerDetails__header__more--desktop"],
                            )}
                        >
                            {badge && <Dot />}
                            {details}
                        </span>
                    </div>
                </div>
                <div className={styles["SellerDetails__header__like--desktop"]}>{likeButton}</div>
            </div>
            <div
                className={cx(
                    styles.SellerDetails__header__more,
                    styles["SellerDetails__header__more--mobile"],
                )}
            >
                {details}
            </div>
            <div className={styles["SellerDetails__header__like--mobile"]}>{likeButton}</div>

            {seller.terms &&
                seller.terms.trim() &&
                (!seller.hasBlockedBazaar || isSeller || isAdmin) && (
                    <div className={styles.SellerDetails__terms}>
                        <TrimContent
                            collapseLabel={BZ2_HIDE_TERMS}
                            expandLabel={BZ2_SHOW_COMPLETE_TERMS}
                            height={75}
                        >
                            {linebreaksbr(seller.terms.trim())}
                        </TrimContent>
                    </div>
                )}
        </Panel>
    );
};

SellerDetails.displayName = "SellerDetails";

export default React.memo<Props>(SellerDetails);
